import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {AuthGuard} from 'src/app/common/auth-guard.service';
import {Machine} from 'src/app/model/machine';
import {MachineDetailsService} from 'src/app/services/machine-details.service';
import {AuditFilters} from 'src/app/model/filters/audit-filters';
import {Audit} from 'src/app/model/audit-model';
import {HierarchyService} from 'src/app/services/hierarchy.service';
import {AuditsService} from 'src/app/services/audits.service';
import {TranslocoService} from '@ngneat/transloco';
import {TreeNode} from 'primeng/api';
import {TimezoneService} from "../../../../services/timezone.service";
import {AuditAction} from "../../../../model/audit-action";
import {ExportFileType} from "../../../../model/export-file-type";

@Component({
  selector: 'app-machine-details-audits',
  templateUrl: './machine-details-audits.component.html',
  styleUrls: ['./machine-details-audits.component.scss']
})
export class MachineDetailsAuditsComponent implements OnInit, OnDestroy {

  public language: string;

  public audits: Audit[];
  public totalAudits: number;
  public loadingAudits: boolean = true;

  public rows: number = 10;
  public first: number = 0;

  public exportVisible: boolean;
  public filtersVisible: boolean;
  public currentFilters: AuditFilters;

  public hierarchy: TreeNode[];
  public organizations: any[];

  public availableYearRange: string;
  public rangeDates: Date[];

  public machine: Machine;

  private isMachineLoading: boolean = true;

  private machineDetailsSubscription: Subscription;


  constructor(
    private readonly machineDetailsService: MachineDetailsService,
    private auditsService: AuditsService,
    private hierarchyService: HierarchyService,
    private i18n: TranslocoService,
    private readonly timezoneService: TimezoneService,
    public authGuard: AuthGuard
  ) {
    this.currentFilters = new AuditFilters();
    this.availableYearRange = this.timezoneService.calcYearRange();
  }

  ngOnInit(): void {
    this.machineDetailsSubscription = this.machineDetailsService.$notifier.subscribe(machineDetails => {
      this.machine = machineDetails.machine;
      this.isMachineLoading = machineDetails.machineLoading;

      this.loadAudits(this.first / this.rows, this.rows)
    })

    this.language = this.i18n.getActiveLang();
    this.loadFilters();
  }

  loadAudits(offset?: number, limit?: number): void {
    if (!this.isMachineLoading) {
      this.loadingAudits = true;
      this.audits = new Array(this.rows);

      this.trimFieldsForm(this.currentFilters);
      this.auditsService.getMachineAuditsBySerialNumber(this.machine.serial, offset, limit, this.currentFilters).subscribe(response => {
        this.audits = response.data.content.map(audit => {
          if (this.hasTranslation(audit)) {
            audit.newValue = MachineDetailsAuditsComponent.parseJson(audit.newValue);
            audit.oldValue = MachineDetailsAuditsComponent.parseJson(audit.oldValue);
          } else if (audit.attributeName === 'machines' || audit.attributeName === 'permissions' || audit.attributeName === 'requieredActions') {
            audit.newValue = audit.newValue.slice(1, -1);
            audit.oldValue = audit.oldValue.slice(1, -1);
          }
          return audit;
        });
        this.totalAudits = response.data.totalElements;
        this.loadingAudits = false;
      });
    }
  }

  private static parseJson(value: any): string | null {
    if (value != null) {
      try {
        return JSON.parse(value);
      } catch (e) {
        return value;
      }
    } else return null;
  }

  trimFieldsForm(filters: AuditFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  onPageChange(event): void {
    this.loadAudits(event.first / event.rows, event.rows);
  }

  hasTranslation(audit: Audit): boolean {
    return audit.attributeName === 'country' || audit.attributeName === 'locality' || audit.attributeName === 'state';
  }

  getAttributeValue(value: any): string {
    if (!this.loadingAudits && value != undefined) {
      if (value.hasOwnProperty(this.language)) return value[this.language];
      else return value;
    }
    return null;
  }

  private loadFilters(): void {
    this.buildTreeNode();
  }

  private buildTreeNode(): void {
    this.hierarchyService.getHierarchy().subscribe(response => {
      this.hierarchy = this.hierarchyService.getHierarchyTreeNodeFilter(response.hierarchyDto)[1];
    });
  }

  resetFilters(): void {
    this.currentFilters = new AuditFilters();
    this.rangeDates = undefined;
    this.organizations = [];
  }

  applyFilters(): void {
    this.first = 0;
    if (this.organizations != null && this.organizations.length > 0) this.currentFilters.organizationUuids = this.organizations.map(organization => organization.data);
    else delete this.currentFilters['organizationUuids'];
    if (this.rangeDates) {
      let periodRange: { from: number, until: number };
      this.currentFilters.from = this.rangeDates[0].getTime();
      if (this.rangeDates.length == 1 || this.rangeDates[1] == null) {
        periodRange = this.timezoneService.timestampRangeFromCalendarData([this.rangeDates[0], this.rangeDates[0]]);
      } else {
        periodRange = this.timezoneService.timestampRangeFromCalendarData([this.rangeDates[0], this.rangeDates[1]]);
      }
      this.currentFilters.from = periodRange.from;
      this.currentFilters.until = periodRange.until;
    } else if (this.currentFilters.from) {
      this.currentFilters.from = null;
      this.currentFilters.until = null;
    }
    this.loadAudits(this.first / this.rows, this.rows);
  }

  ngOnDestroy(): void {
    if (this.machineDetailsSubscription != null) this.machineDetailsSubscription.unsubscribe();
  }

  protected readonly AuditAction = AuditAction;
  public readonly ExportFileType = ExportFileType;
}
